import { useEffect, useMemo, useRef, useState } from 'react'
import { Box, Button, Input, Text, VStack } from '@chakra-ui/react'
import { useGeoDistricts } from '../../hooks/useGeoDistricts'
import { districtUnitTypeFor, filterDistricts, type GeoDistrict } from './districtCatalog'
import type { Chamber } from '../../types/lobbyDay'

interface DistrictComboboxProps {
  chamber: Chamber
  state: string
  value: string
  onChange: (district: string) => void
  invalid?: boolean
  id?: string
}

/**
 * District entry backed by the GeoUnits catalogue.
 *
 * Stores the canonical district STRING, never a GeoUnit id: GeoUnits are vintaged, so an FK
 * would pin a legislator to a boundary year and would make committee-scoped Legislator depend
 * on a table that deliberately is not committee-scoped. DistrictMatcher stays the matching
 * engine — this only guarantees it gets a real district as input.
 *
 * Free text is always accepted. Boundaries are seeded per state and Local 400 spans seven, so
 * an unseeded state must not block roster building; it degrades to a plain field plus a note,
 * and upgrades to validated entry the moment that state's boundaries load.
 */
export function DistrictCombobox({
  chamber, state, value, onChange, invalid, id = 'legislator-district',
}: DistrictComboboxProps) {
  const unitType = districtUnitTypeFor(chamber)
  const { data: catalog, isLoading, error } = useGeoDistricts(unitType, state)
  const [text, setText] = useState(value)
  const [open, setOpen] = useState(false)
  const [focused, setFocused] = useState(false)

  // A pointer pick fires mousedown (which commits and closes the list) and then, on the same
  // interaction, a trailing click on the DOM node that was under the pointer. Guard so the same
  // pick is not committed twice; reset it whenever the list opens for a fresh interaction.
  const pickedRef = useRef(false)

  // Tracks the `value` PROP itself across renders -- not `text`, and not what we last emitted.
  // Comparing against `text` (the first cut of this fix) is wrong: after this component commits
  // its own edit (blur/Enter/pick calling `onChange`), `text` already holds the new value but the
  // prop takes at least one more render to catch up if the parent doesn't echo it back at all
  // (e.g. a bare test spy, or a parent that only stores the value elsewhere). Comparing against
  // `value !== text` in that case reads as "external change" on every subsequent render and
  // reverts the input to the stale prop right after the user's edit committed. Comparing the raw
  // prop against its own previous value is immune to that: if the parent never echoes, `value`
  // literally never changes, so there is nothing to resync. See EmployerFilter.tsx's
  // `lastEmittedRef` for the same distinction solved for an async/object-valued field; a plain
  // previous-prop ref is sufficient here because every commit path in this component is
  // synchronous.
  const prevValueRef = useRef(value)

  // An external `value` change that arrives while the field is focused or its dropdown is open
  // can't be applied to `text` immediately (that would clobber whatever the user is doing), but
  // it also must not be silently dropped -- e.g. LegislatorFormModal populating an edit form via
  // reset()/async defaultValues that lands after this control mounted and already has focus
  // (autofocus, or the user tabbing in before the fetch resolves). So it's held here and resolved
  // on blur: applied if the user never typed since it arrived, discarded (in favor of the user's
  // own typed text) if they did.
  const pendingExternalRef = useRef<string | null>(null)
  // True once the user has typed a character during the CURRENT focused session. Reset on every
  // `onFocus` so it reflects "did anything change during this visit to the field", independent of
  // whether that typing happened before or after a pending external value showed up.
  const dirtyRef = useRef(false)

  useEffect(() => {
    const externalChange = value !== prevValueRef.current
    prevValueRef.current = value

    if (externalChange) {
      if (focused || open) {
        // Mid-interaction: defer. Left for onBlur/onKeyDown(Enter) to resolve once it's known
        // whether the user typed over it.
        pendingExternalRef.current = value
      } else {
        // Idle: nothing in flight to conflict with, apply immediately.
        pendingExternalRef.current = null
        setText(value)
      }
      return
    }

    // No fresh external value this render. But if the field just went idle (this run was
    // triggered by `focused`/`open` flipping to false) and a deferred value is still waiting --
    // meaning onBlur found the user hadn't typed over it and deliberately left it for us --
    // apply it now.
    if (!focused && !open && pendingExternalRef.current !== null) {
      setText(pendingExternalRef.current)
      pendingExternalRef.current = null
    }
  }, [value, focused, open])

  const rows = useMemo(
    () => filterDistricts(catalog ?? [], text, 8),
    [catalog, text],
  )

  const hasCatalog = (catalog?.length ?? 0) > 0
  // `catalog` is undefined (so hasCatalog is false) both for a genuinely unseeded state and for
  // a seeded state whose first fetch just hasn't resolved yet -- those two cases are otherwise
  // indistinguishable. Suppress the note during the in-flight window so it never flashes for a
  // state that IS seeded. Also suppress it on a fetch ERROR: a 500/401/network failure is not
  // "no boundaries for this state" -- telling the user that would be a confident false statement
  // that steers them away from a picker that does exist. showErrorNote below covers that case.
  const showUnseededNote = !hasCatalog && !isLoading && !error && state.trim().length === 2
  const showErrorNote = !!error && !isLoading && state.trim().length === 2

  const pick = (d: GeoDistrict) => {
    if (pickedRef.current) return
    pickedRef.current = true
    pendingExternalRef.current = null
    dirtyRef.current = false
    setText(d.name)
    setOpen(false)
    onChange(d.name)
  }

  return (
    <Box position="relative" w="full">
      <Input
        id={id}
        aria-label="District"
        aria-invalid={invalid || undefined}
        value={text}
        placeholder={hasCatalog ? 'Start typing a district…' : 'Third Middlesex'}
        minH="44px"
        onFocus={() => { setFocused(true); dirtyRef.current = false }}
        onChange={(e) => {
          setText(e.target.value)
          setOpen(true)
          pickedRef.current = false
          dirtyRef.current = true
        }}
        onBlur={() => {
          setFocused(false)
          setOpen(false)
          // A mousedown pick already committed and reset text to the picked name; onBlur firing
          // after it would otherwise overwrite that with the same name, which is harmless, but
          // guard anyway so a blur that races a pick never commits stale partial text.
          if (pickedRef.current) return
          if (pendingExternalRef.current !== null && !dirtyRef.current) {
            // A deferred external value arrived during this session and the user never typed
            // over it -- leave it for the resync effect (which runs right after this state
            // settles) to apply. Committing the untouched local text now would push the OLD
            // value back up and fight the very external change we're about to adopt.
            return
          }
          pendingExternalRef.current = null // the user's own edit supersedes any deferred value
          onChange(text.trim())
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault()
            setOpen(false)
            if (pendingExternalRef.current !== null && !dirtyRef.current) return
            pendingExternalRef.current = null
            onChange(text.trim())
          }
          if (e.key === 'Escape') setOpen(false)
        }}
      />
      {showUnseededNote && (
        <Text fontSize="xs" color="gray.500" mt={1}>
          No district boundaries loaded for {state.trim().toUpperCase()} — type the district as
          the state writes it.
        </Text>
      )}
      {showErrorNote && (
        <Text fontSize="xs" color="red.500" mt={1}>
          Couldn't check districts for {state.trim().toUpperCase()} — type the district as the
          state writes it.
        </Text>
      )}
      {open && hasCatalog && rows.length > 0 && (
        <VStack
          align="stretch" gap={0} mt={1} bg="surface" borderWidth="1px" borderColor="gray.200"
          borderRadius="md" shadow="md" position="absolute" zIndex={20} w="full"
          maxH="min(240px, 40vh)" overflowY="auto">
          {rows.map((d) => (
            <Button
              key={d.code} variant="ghost" justifyContent="flex-start" minH="44px"
              borderRadius={0}
              // Pointer path: mousedown fires before the input's blur, so preventDefault keeps
              // focus on the input (no blur), and the pick commits here first. Keyboard path:
              // Enter/Space on this <button> dispatches a bare click with no preceding mousedown,
              // so onClick is the only handler that path reaches. Both converge on pick(), which
              // is idempotent per interaction via pickedRef.
              onMouseDown={(e) => { e.preventDefault(); pick(d) }}
              onClick={() => pick(d)}>
              {d.name}
            </Button>
          ))}
        </VStack>
      )}
    </Box>
  )
}
